import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, ShoppingBag, Heart, Truck, Shield, Sparkles } from "lucide-react";
import { getProduct } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { SmartImage } from "@/components/SmartImage";
import { buildWhatsAppSingleProduct, openWhatsApp } from "@/lib/whatsapp";
import { toast } from "sonner";

export const ProductDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem, setIsOpen } = useCart();

  useEffect(() => {
    getProduct(slug).then((p) => { setProduct(p); setActiveImg(0); }).catch(() => toast.error("Product not found"));
  }, [slug]);

  if (!product) return <div className="max-w-6xl mx-auto px-4 py-20 text-[#8E6377]">Loading...</div>;

  const images = product.images?.length ? product.images : [""];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-16" data-testid="product-detail-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#8E6377] hover:text-[#D81B60] mb-4">
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="grid md:grid-cols-2 gap-6 md:gap-10">
        <div>
          <div className="relative rounded-3xl overflow-hidden bg-[#FDF2F5]">
            {product.discount_percent > 0 && (
              <span className="absolute top-4 left-4 z-10 bg-[#FF0055] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                {product.discount_percent}% OFF
              </span>
            )}
            <SmartImage
              src={images[activeImg]}
              alt={product.name}
              width={900}
              widths={[400, 600, 800, 1000, 1200]}
              sizes="(max-width: 768px) 100vw, 50vw"
              priority
              className="w-full aspect-square object-cover"
              aspectRatio="1/1"
              data-testid="product-main-image"
            />
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto hide-scrollbar" data-testid="product-thumbnails">
              {images.map((src, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all ${i === activeImg ? "border-[#D81B60]" : "border-transparent opacity-70 hover:opacity-100"}`}
                >
                  <SmartImage
                    src={src}
                    alt=""
                    width={128}
                    widths={[80, 128, 200]}
                    sizes="64px"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#D81B60] font-bold">{product.category}</p>
          <h1 className="font-serif text-4xl text-[#2D0A22] mt-2 leading-tight" data-testid="product-name">{product.name}</h1>
          <p className="text-[#8E6377] mt-3 leading-relaxed">{product.description || product.short_desc}</p>

          <div className="flex items-baseline gap-3 mt-6">
            <span className="text-3xl font-bold text-[#D81B60]" data-testid="product-price">₹{product.price.toLocaleString("en-IN")}</span>
            {product.original_price && <span className="text-lg line-through text-[#8E6377]">₹{product.original_price.toLocaleString("en-IN")}</span>}
          </div>

          <div className="flex items-center gap-3 mt-6">
            <div className="flex items-center bg-[#FCE4EC] rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 rounded-full text-[#D81B60] font-bold">−</button>
              <span data-testid="product-qty" className="w-8 text-center font-semibold">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-10 h-10 rounded-full text-[#D81B60] font-bold">+</button>
            </div>
            <button
              data-testid="detail-add-to-cart-button"
              onClick={() => { addItem(product, qty); toast.success(`Added ${qty} × ${product.name}`); }}
              className="flex-1 py-3 rounded-full bg-[#FCE4EC] text-[#D81B60] font-semibold hover:bg-[#F8BBD0] flex items-center justify-center gap-2"
            >
              <ShoppingBag size={18} /> Add to Bucket
            </button>
          </div>

          <div className="mt-3 flex gap-3">
            <button
              data-testid="detail-buy-now-button"
              onClick={() => { addItem(product, qty); setIsOpen(true); }}
              className="flex-1 pill-btn inline-flex justify-center items-center gap-2"
            >
              Buy Now
            </button>
            <button
              data-testid="detail-whatsapp-button"
              onClick={() => openWhatsApp(buildWhatsAppSingleProduct(product))}
              className="flex-1 py-3 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white font-semibold flex items-center justify-center gap-2"
            >
              <MessageCircle size={18} /> WhatsApp
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3">
            <div className="p-3 rounded-2xl bg-[#FDF2F5] border border-[#F4D8E2] text-center">
              <Truck className="text-[#D81B60] mx-auto mb-1" size={18} />
              <p className="text-[11px] text-[#2D0A22] font-medium">Pan-India Delivery</p>
            </div>
            <div className="p-3 rounded-2xl bg-[#FDF2F5] border border-[#F4D8E2] text-center">
              <Heart className="text-[#D81B60] mx-auto mb-1" size={18} />
              <p className="text-[11px] text-[#2D0A22] font-medium">Handmade with Love</p>
            </div>
            <div className="p-3 rounded-2xl bg-[#FDF2F5] border border-[#F4D8E2] text-center">
              <Shield className="text-[#D81B60] mx-auto mb-1" size={18} />
              <p className="text-[11px] text-[#2D0A22] font-medium">Never Wilts</p>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-2xl bg-gradient-to-br from-[#FCE4EC] to-[#F8BBD0] flex items-center gap-3">
            <Sparkles className="text-[#AD1457]" size={20} />
            <div>
              <p className="text-sm font-semibold text-[#AD1457]">Use code SUGANDHI10</p>
              <p className="text-xs text-[#8E6377]">Get 10% off at checkout</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
